//! This replaces CellInput.tsx as the inline cell editor for the grid. It uses
//! the shared CellEditorView surface (same UI as the formula bar).

import { hasPermissionToEditFile } from '@/app/actions';
import { Action } from '@/app/actions/actions';
import { defaultActionSpec } from '@/app/actions/defaultActionsSpec';
import { clearFormulaHighlights, updateFormulaHighlights } from '@/app/cellEditor/cellEditorFormula';
import { trackFunctionsFromFormula } from '@/app/cellEditor/recentFormulaFunctions';
import { events } from '@/app/events/events';
import { openCodeEditor as openCodeEditorForCell } from '@/app/grid/actions/openCodeEditor';
import { sheets } from '@/app/grid/controller/Sheets';
import { applyDeltaSkippingHidden } from '@/app/grid/sheet/hiddenNavigation';
import { getAlternatingBgOpacity } from '@/app/gridGL/cells/CellsFills';
import type { ChangeInputOptions } from '@/app/gridGL/HTMLGrid/inlineEditor/changeInputOptions';
import { inlineEditorEvents } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorEvents';
import { inlineEditorFormula } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorFormula';
import {
  EMOJI_FONTS,
  inlineEditorInput,
  PADDING_FOR_INLINE_EDITOR,
  type InlineEditorViewportBounds,
} from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorInput';
import { CursorMode, inlineEditorKeyboard } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorKeyboard';
import { inlineEditorSpans, type SpanFormatting } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorSpans';
import { READ_ONLY_CODE_TABLE_ACTION_BAR_RESERVED_HEIGHT } from '@/app/gridGL/HTMLGrid/inlineEditor/readOnlyCodeTableConstants';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { getFrozenPaneForCell } from '@/app/gridGL/pixiApp/frozenLayout';
import { frozenPanes } from '@/app/gridGL/pixiApp/FrozenPanes';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { CURSOR_THICKNESS } from '@/app/gridGL/UI/Cursor';
import {
  blendTintOverHex,
  convertColorStringToHex,
  convertTintToHex,
  getCSSVariableTint,
} from '@/app/helpers/convertColor';
import { focusGrid } from '@/app/helpers/focusGrid';
import { isMobileMode } from '@/app/helpers/isMobile';
import type {
  CellAlign,
  CellFormatSummary,
  CellWrap,
  JsCoordinate,
  JsRenderCodeCell,
  TextSpan,
} from '@/app/quadratic-core-types';
import { xyToA1 } from '@/app/quadratic-core/quadratic_core';
import type { CodeCell } from '@/app/shared/types/codeCell';
import type { SheetPosTS } from '@/app/shared/types/size';
import { colors } from '@/app/theme/colors';
import { createFormulaStyleHighlights } from '@/app/ui/menus/CodeEditor/hooks/useEditorCellHighlights';
import { multiplayer } from '@/app/web-workers/multiplayerWebWorker/multiplayer';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { LINE_HEIGHT, OPEN_SANS_FIX } from '@/app/web-workers/renderWebWorker/worker/cellsLabel/CellLabel';
import { CELL_HEIGHT, DEFAULT_FONT_SIZE, ZOOM_ANIMATION_TIME_MS } from '@/shared/constants/gridConstants';
import { sharedEvents } from '@/shared/sharedEvents';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import BigNumber from 'bignumber.js';
import { Point, Rectangle } from 'pixi.js';

// Distance in screen px the editor must be pulled from its cell before the
// detached cell-reference label appears (avoids flicker on tiny sub-pixel drift).
const DETACHED_THRESHOLD = 1;

// Determines whether the cell at `pos` is the anchor of a multi-cell code/data
// table (spans more than one cell or renders table UI like the name/columns
// header). Writing a static value onto such an anchor would replace and destroy
// the entire table, so callers use this to refuse a stray value edit there.
// Formula cells with multi-cell output are excluded: they open in the inline
// formula editor at the anchor.
const isMultiCellTableAnchor = (pos: JsCoordinate): boolean => {
  const anchorCodeCell = content.cellsSheet.tables.getCodeCellIntersects(pos);
  return (
    !!anchorCodeCell &&
    anchorCodeCell.language !== 'Formula' &&
    anchorCodeCell.x === pos.x &&
    anchorCodeCell.y === pos.y &&
    (anchorCodeCell.w > 1 || anchorCodeCell.h > 1 || anchorCodeCell.show_name || anchorCodeCell.show_columns)
  );
};

class InlineEditorHandler {
  private div?: HTMLDivElement;

  private open = false;
  private showing = false;

  private initialValue = '';

  // The text the user typed to open the editor (empty when opening an existing
  // value for editing, e.g. via double-click/F2). Used by the undo history to
  // treat that first typed text as an undoable edit rather than a fixed baseline.
  get typedPrefix(): string {
    return this.initialValue;
  }

  x = 0;
  y = 0;
  width = 0;
  height = 0;
  // Screen-space position of the editor's top-left captured during the last full
  // layout. While the user scrolls, onViewportChanged keeps the editor glued to
  // this position via a CSS transform so it stays where editing started instead
  // of scrolling away with (or under) the grid — matching Google Sheets, which
  // detaches the inline editor on scroll. This both keeps the editor on screen
  // and prevents it from sliding over the frozen panes. Recomputing from this
  // baseline avoids re-running the (expensive) Monaco text layout or churning
  // React state every frame.
  private pinScreenX = 0;
  private pinScreenY = 0;
  // The viewport center and scale captured at the last full layout while the
  // editor was attached to its cell (i.e. before the user scrolled/zoomed away).
  // returnToCell() animates the viewport back to this exact state so the cell
  // re-aligns under the floating editor at the zoom it was being edited at.
  private attachedViewport?: { centerX: number; centerY: number; scale: number };
  // Whether the editor is currently held away from its cell because the user
  // scrolled the viewport off the position where editing started. Mirrored into
  // the atom only on transitions so the per-frame scroll handler does not churn
  // React state.
  private detached = false;
  // Viewport scale captured at the moment the editor detached. While detached,
  // the editor and its border are locked to this zoom (counter-scaled against
  // the live viewport zoom) so they stay a constant on-screen size and keep
  // fitting, even as the grid zooms behind them. Reset to 1 once re-attached.
  private lockedScale = 1;
  // The cell being edited (anchor for merged cells). Use this for all data operations
  // (reading/writing cell values, formula operations, validation, etc.)
  location?: SheetPosTS;
  // The original cursor position where the user clicked. Use this for UI positioning
  // (editor placement, popup positioning, etc.). For merged cells, this may differ from location.
  cursorLocation?: SheetPosTS;
  formula: boolean | undefined = undefined;
  readOnly = false;
  private tableCodeCell?: CodeCell;

  cursorIsMoving = false;

  private formatSummary?: CellFormatSummary;
  temporaryBold: boolean | undefined;
  temporaryItalic: boolean | undefined;
  temporaryUnderline: boolean | undefined;
  temporaryStrikeThrough: boolean | undefined;

  private codeCell?: JsRenderCodeCell;

  constructor() {
    events.on('changeInput', this.changeInput);
    events.on('changeSheet', this.changeSheet);
    events.on('sheetOffsetsUpdated', this.sheetOffsets);
    events.on('resizeHeadingColumn', this.sheetOffsets);
    events.on('resizeHeadingRow', this.sheetOffsets);
    events.on('viewportChangedReady', this.onViewportChanged);
    events.on('contextMenu', this.closeIfOpen);
    inlineEditorEvents.on('replaceText', this.replaceText);
    sharedEvents.on('changeThemeAppearanceMode', this.onThemeChange);
    createFormulaStyleHighlights();
  }

  private onThemeChange = () => {
    if (this.open) {
      // Always recompute: the alternating-row overlay is derived from a
      // theme-dependent tint, so it must be re-blended even when the cell has
      // an explicit fill color.
      inlineEditorInput.setBackgroundColor(this.getCellBackgroundColor());
    }
    if (this.open && !this.formatSummary?.textColor) {
      inlineEditorInput.setTextColor(convertTintToHex(colors.cellFontColor));
    }
    if (this.open) {
      inlineEditorInput.setCursorColor(convertTintToHex(colors.cellFontColor));
    }
  };

  // Whether the cell being edited falls on an alternating-color row of its code
  // table. Mirrors the stripe logic in CellsFills.drawAlternatingColors so the
  // editor background matches the rendered cell.
  private isAlternatingRow = (): boolean => {
    const table = this.codeCell;
    if (!table || !table.alternating_colors || table.h <= 1 || table.is_html_image || !this.location) {
      return false;
    }
    const relativeRow = this.location.y - table.y;
    if (relativeRow < 0) return false;
    return relativeRow % 2 !== (table.show_name !== table.show_columns ? 1 : 0);
  };

  // Background color the editor should use to match the rendered cell, including
  // the semi-transparent alternating-row overlay that the grid paints on tables.
  private getCellBackgroundColor = (): string => {
    const base = this.formatSummary?.fillColor
      ? convertColorStringToHex(this.formatSummary.fillColor)
      : convertTintToHex(colors.gridBackground);
    if (this.isAlternatingRow()) {
      return blendTintOverHex(base, getCSSVariableTint('foreground'), getAlternatingBgOpacity());
    }
    return base;
  };

  private sheetOffsets = (sheetId: string) => {
    if (this.location?.sheetId === sheetId) {
      if (this.open) {
        this.updateMonacoCellLayout();
      }
    }
  };

  // World-space bounds of the cell the editor is anchored to (the cursor cell,
  // expanded to the full merged-cell rect when applicable).
  private getCursorCellBounds = (): Rectangle | undefined => {
    if (!this.cursorLocation) return undefined;
    const sheet = sheets.sheet;
    const mergeRect = sheet.getMergeCellRect(this.cursorLocation.x, this.cursorLocation.y);
    if (mergeRect) {
      return sheet.getScreenRectangle(
        Number(mergeRect.min.x),
        Number(mergeRect.min.y),
        Number(mergeRect.max.x) - Number(mergeRect.min.x) + 1,
        Number(mergeRect.max.y) - Number(mergeRect.min.y) + 1
      );
    }
    return sheet.getCellOffsets(this.cursorLocation.x, this.cursorLocation.y);
  };

  // Keeps the editor pinned to the screen position where editing started while
  // the user scrolls (Google Sheets detaches the inline editor on scroll). The
  // editor div lives in the viewport-transformed overlay layer, so by default it
  // rides the grid on scroll — which lets it slide over the frozen panes or off
  // screen entirely. Instead of letting it follow the cell, we hold it in place.
  //
  // this.x/this.y are React-controlled world coordinates that stay constant
  // during a scroll, and this.pinScreenX/Y is their screen position captured at
  // the last full layout. Each frame we measure where that world point now maps
  // on screen under the current viewport transform and write a CSS transform
  // (in the div's local/world units) that cancels the difference, keeping the
  // editor visually fixed. We do this synchronously within the same
  // viewportChangedReady tick that repositions the parent overlay transform, so
  // there is no one-frame React lag (which would make the editor drift during
  // momentum/deceleration scrolling). Using transform (which React never
  // manages) also avoids fighting React over left/top, and reuses the baseline
  // captured by the last full layout, so no Monaco text layout runs per frame.
  private onViewportChanged = () => {
    if (!this.open || !this.showing || !this.div || !this.cursorLocation) return;
    if (sheets.current !== this.cursorLocation.sheetId) return;

    pixiApp.viewport.updateTransform();
    const wt = pixiApp.viewport.worldTransform;
    const dx = (this.pinScreenX - (wt.a * this.x + wt.tx)) / wt.a;
    const dy = (this.pinScreenY - (wt.d * this.y + wt.ty)) / wt.d;

    // Show the cell-reference label and lock the zoom once the editor has been
    // pulled a noticeable distance from its cell (in screen px); release when the
    // user scrolls back to the starting location. The lock scale is captured on
    // the transition (when dx/dy are still ~0), so locking starts seamlessly.
    this.setDetached(Math.abs(dx * wt.a) >= DETACHED_THRESHOLD || Math.abs(dy * wt.d) >= DETACHED_THRESHOLD);

    const scale = this.detached ? this.lockedScale / wt.a : 1;
    this.div.style.transform = dx || dy || scale !== 1 ? `translate(${dx}px, ${dy}px) scale(${scale})` : '';
  };

  // Animates the viewport so the editing cell slides back under the floating
  // (detached) editor and the zoom returns to the locked level, re-attaching it
  // at the size it was being edited at. Triggered by clicking the detached
  // cell-reference label, the easiest way back after a pan/zoom.
  returnToCell = () => {
    if (!this.open || !this.attachedViewport) return;
    const viewport = pixiApp.viewport;
    const { centerX, centerY, scale } = this.attachedViewport;

    viewport.animate({
      position: new Point(centerX, centerY),
      scale,
      time: ZOOM_ANIMATION_TIME_MS,
      ease: 'easeInOutSine',
      removeOnInterrupt: true,
      // When the viewport reaches the original position the cell sits back under
      // the editor; re-attach so the floating cell-reference label disappears
      // and the editor resumes following its cell. A fresh layout (now that
      // detached is false) re-establishes the pin baseline at the rest position.
      callbackOnComplete: () => {
        if (!this.open) return;
        this.setDetached(false);
        this.updateMonacoCellLayout();
      },
    });
    pixiApp.viewportChanged();
  };

  // Updates the detached flag, pushing to React state only on a transition and
  // capturing the zoom to lock to when first detaching.
  private setDetached = (detached: boolean) => {
    if (this.detached === detached) return;
    this.detached = detached;
    if (detached) {
      this.lockedScale = pixiApp.viewport.scale.x;
    } else {
      this.lockedScale = 1;
    }
    // The editor cell's rendered label is hidden while editing so it doesn't
    // show behind the editor. Once detached (scrolled away), the cell is no
    // longer covered, so reveal its previous content; hide it again on
    // re-attach.
    const input = gridSettings.getInput();
    if (input.show && input.x !== undefined && input.y !== undefined && input.sheetId !== undefined) {
      content.cellsSheets.showLabel(input.x, input.y, input.sheetId, detached);
    }
    gridSettings.setInlineEditorState((prev) => ({ ...prev, detached }));
  };

  // Resets state after editing is complete.
  private reset() {
    this.open = false;
    this.initialValue = '';
    inlineEditorInput.set('');
    inlineEditorEvents.emit('status', false);
    inlineEditorEvents.emit('selectionFormatting', undefined);
    this.cursorIsMoving = false;
    this.x = this.y = this.width = this.height = 0;
    this.pinScreenX = this.pinScreenY = 0;
    this.attachedViewport = undefined;
    this.setDetached(false);
    if (this.div) {
      this.div.style.transform = '';
    }
    this.location = undefined;
    this.cursorLocation = undefined;
    this.formula = undefined;
    this.readOnly = false;
    this.tableCodeCell = undefined;
    inlineEditorInput.setReadOnly(false);
    this.formatSummary = undefined;
    this.temporaryBold = undefined;
    this.temporaryItalic = undefined;
    this.temporaryUnderline = undefined;
    this.temporaryStrikeThrough = undefined;
    this.codeCell = undefined;
    this.changeToFormula(false);
    inlineEditorKeyboard.resetKeyboardPosition();
    inlineEditorFormula.clearDecorations();
    // Remove the grid cell-reference highlights (dashed boxes) that the formula
    // editor draws; otherwise they linger after the editor closes.
    clearFormulaHighlights();
    inlineEditorSpans.clear();
    window.removeEventListener('keydown', inlineEditorKeyboard.keyDownFromWindow);
    multiplayer.sendEndCellEdit();
    // Drop focus from the inline editor textarea before hiding the editor so
    // that focusGrid() can move focus to the grid canvas. The editor is hidden
    // on a later React render, so if it still held focus the browser would move
    // it to <body>, leaving the sheet unfocused.
    inlineEditorInput.blur();
    this.hideDiv();
  }

  // Keeps the cursor visible in the viewport.
  keepCursorVisible = () => {
    if (sheets.current !== this.location?.sheetId || !this.showing) return;

    const sheetRectangle = pixiApp.getViewportRectangle();

    // Build the editor rectangle from the authoritative layout coordinates
    // (world space, the same space as the viewport rectangle) instead of the
    // editor's DOM bounds. On a fast cell switch the editor DOM may not have
    // been repositioned to the new cell yet, so getBoundingClientRect() reports
    // the previously-open cell's location while this.width is the new cell's
    // width — a mismatched rectangle that pans the viewport incorrectly (and
    // then feeds back into the layout, growing the box). this.x/y/width/height
    // are set synchronously by updateMonacoCellLayout and are always consistent.
    const editorRectangle = new Rectangle(this.x, this.y, this.width, this.height);

    // A cell in a frozen pane is pinned and always visible, so panning the body
    // to "reveal" it is unnecessary and would drag the body away. Skip the
    // adjustment on whichever axis the cell is frozen.
    const sheet = sheets.sheet;
    const pane =
      this.cursorLocation && (sheet.frozenColumns > 0 || sheet.frozenRows > 0)
        ? getFrozenPaneForCell(this.cursorLocation.x, this.cursorLocation.y)
        : 'body';
    const frozenX = pane === 'left' || pane === 'corner';
    const frozenY = pane === 'top' || pane === 'corner';

    let x = 0;
    if (!frozenX) {
      if (editorRectangle.left - PADDING_FOR_INLINE_EDITOR <= sheetRectangle.left) {
        x = sheetRectangle.left - (editorRectangle.left - PADDING_FOR_INLINE_EDITOR);
      } else if (editorRectangle.right + PADDING_FOR_INLINE_EDITOR >= sheetRectangle.right) {
        x = sheetRectangle.right - (editorRectangle.right + PADDING_FOR_INLINE_EDITOR);
      }
    }

    let y = 0;
    if (!frozenY) {
      // check if the editor is too tall to fit in the viewport
      if (editorRectangle.height + PADDING_FOR_INLINE_EDITOR <= sheetRectangle.height) {
        // keep the editor in view
        if (editorRectangle.top - PADDING_FOR_INLINE_EDITOR <= sheetRectangle.top) {
          y = sheetRectangle.top - (editorRectangle.top - PADDING_FOR_INLINE_EDITOR);
        } else if (editorRectangle.bottom + PADDING_FOR_INLINE_EDITOR >= sheetRectangle.bottom) {
          y = sheetRectangle.bottom - (editorRectangle.bottom + PADDING_FOR_INLINE_EDITOR);
        }
      } else {
        // Editor is taller than the viewport: keep the cursor in view. The cursor
        // offset is reported in the editor's local pixels, which match world units
        // (the cell-edit container is scaled by the viewport), so add it to this.y.
        const { position } = inlineEditorInput.getEditorSizing();
        const cursorTop = this.y + position.top;
        const cursorBottom = cursorTop + position.height;
        if (cursorTop < sheetRectangle.top) {
          y = sheetRectangle.top - cursorTop;
        } else if (cursorBottom > sheetRectangle.bottom) {
          y = sheetRectangle.bottom - cursorBottom;
        }
      }
    }

    if (x || y) {
      const scale = pixiApp.viewport.scale.x;
      const { width, height } = content.headings.headingSize;
      const previousX = pixiApp.viewport.x;
      const previousY = pixiApp.viewport.y;
      pixiApp.viewport.x = Math.min(previousX + x * scale, width);
      pixiApp.viewport.y = Math.min(previousY + y * scale, height);
      // This pan is intentional (revealing the editor/cursor), so move the
      // screen-pin baseline by the same amount. Without this, onViewportChanged
      // would treat the pan as a user scroll and hold the editor in place,
      // canceling the reveal. The viewport translation is in screen px, which is
      // exactly the editor's on-screen displacement.
      this.pinScreenX += pixiApp.viewport.x - previousX;
      this.pinScreenY += pixiApp.viewport.y - previousY;
      pixiApp.setViewportDirty();
    }
  };

  // A formula edit survives switching sheets so the user can point-and-click
  // references on another sheet (Excel behavior); any other edit is committed.
  private changeSheet = () => {
    if (!this.div || !this.location || !this.open) return;
    if (this.formula) {
      if (sheets.current !== this.location.sheetId) {
        this.hideDivOffSheet();
        // Clicking the sheet tab moves DOM focus to the body. Refocus the
        // (now invisible) editor so typing continues into the formula, and
        // keep a window-level fallback for keystrokes that land elsewhere
        // (e.g. on the body after clicking non-focusable chrome).
        inlineEditorInput.focus();
        window.removeEventListener('keydown', inlineEditorKeyboard.keyDownFromWindow);
        window.addEventListener('keydown', inlineEditorKeyboard.keyDownFromWindow);
      } else {
        this.showDiv();
        window.removeEventListener('keydown', inlineEditorKeyboard.keyDownFromWindow);
        this.updateMonacoCursorPosition();
      }
    } else {
      this.close({ skipChangeSheet: true });
    }
  };

  // Handler for the changeInput event.
  private changeInput = async (
    input: boolean,
    initialValue?: string,
    cursorMode?: CursorMode,
    options?: ChangeInputOptions
  ) => {
    if (!input && !this.open) {
      return;
    }

    const readOnly = options?.readOnly ?? false;

    // Defense-in-depth: never open the editable inline editor for users that don't
    // have permission to edit the file (e.g. read-only viewers). Without
    // this guard a permission-gated caller could be missed and the user
    // would get a locally-editable inline editor whose changes never sync.
    // Read-only previews (e.g. code table data cells) are allowed for viewers.
    if (input && !readOnly && !hasPermissionToEditFile(gridSettings.getPermissions())) {
      return;
    }

    // On mobile, cell editing happens in MobileCellEditBar instead
    if (input && isMobileMode()) {
      return;
    }

    // A rapid click/drag race can route a stray open of the editable (value)
    // inline editor onto a multi-cell code/data table's anchor. That is never a
    // legitimate value edit: code cells open in the code editor and tables are
    // renamed via their own input. Opening here would let the user commit a
    // static value that replaces and destroys the whole table. Refuse to open
    // on a table anchor. Read-only previews and non-anchor cells (e.g. editing a
    // data cell inside an Import table) are unaffected.
    if (input && !readOnly) {
      const cursor = sheets.sheet.cursor.position;
      if (isMultiCellTableAnchor(cursor)) {
        return;
      }
    }

    if (initialValue) {
      this.initialValue += initialValue;
      initialValue = this.initialValue;
    } else {
      this.initialValue = '';
    }

    if (!this.div) {
      return;
    }

    if (input) {
      this.readOnly = readOnly;
      this.tableCodeCell = readOnly ? options?.tableCodeCell : undefined;

      const sheet = sheets.sheet;
      let cursorPos = sheet.cursor.position;

      // Formula cells with multi-cell output are edited at the anchor cell.
      const formulaCodeCell = content.cellsSheet.tables.getCodeCellIntersects(cursorPos);
      if (formulaCodeCell?.language === 'Formula') {
        if (cursorPos.x !== formulaCodeCell.x || cursorPos.y !== formulaCodeCell.y) {
          sheet.cursor.moveTo(formulaCodeCell.x, formulaCodeCell.y, { checkForTableRef: true });
          cursorPos = sheet.cursor.position;
        }
      }

      // For merged cells, we need to edit the anchor cell (where the value lives)
      // but we'll use the cursor position for positioning the editor
      const mergeRect = sheet.getMergeCellRect(cursorPos.x, cursorPos.y);
      const editLocation =
        formulaCodeCell?.language === 'Formula'
          ? { x: formulaCodeCell.x, y: formulaCodeCell.y }
          : mergeRect
            ? { x: Number(mergeRect.min.x), y: Number(mergeRect.min.y) }
            : { x: cursorPos.x, y: cursorPos.y };

      // Store both the edit location (anchor) and cursor location (for positioning)
      this.location = {
        sheetId: sheet.id,
        x: editLocation.x,
        y: editLocation.y,
      };

      this.cursorLocation = {
        sheetId: sheet.id,
        x: cursorPos.x,
        y: cursorPos.y,
      };

      // Store location locally to avoid race conditions with async operations
      const location = this.location;

      // Helper to check if another cell started editing during async operations
      const locationChanged = () =>
        !this.location ||
        this.location.x !== location.x ||
        this.location.y !== location.y ||
        this.location.sheetId !== location.sheetId;

      this.formatSummary = await quadraticCore.getCellFormatSummary(location.sheetId, location.x, location.y);

      // Check if state was reset or a different cell started editing during the async call
      if (locationChanged()) {
        return;
      }

      let value: string;
      let changeToFormula = false;
      let richTextSpans: TextSpan[] | null = null;
      if (initialValue) {
        changeToFormula = initialValue[0] === '=';

        value = initialValue;
      } else {
        const formula = await quadraticCore.getCodeCell(location.sheetId, location.x, location.y);

        // Check again after async operation
        if (locationChanged()) {
          return;
        }

        if (formula?.language === 'Formula') {
          changeToFormula = true;

          value = '=' + formula.code_string;
        } else {
          changeToFormula = false;

          const jsCellValue = await quadraticCore.getCellValue(location.sheetId, location.x, location.y);

          // Check again after async operation
          if (locationChanged()) {
            return;
          }
          if (jsCellValue) {
            if (jsCellValue.kind === 'Number') {
              this.formatSummary.align = this.formatSummary.align ?? 'right';
            }
            value = jsCellValue.kind === 'Number' ? new BigNumber(jsCellValue.value).toString() : jsCellValue.value;
            if (this.formatSummary?.numericFormat?.type === 'PERCENTAGE') {
              try {
                const number = new BigNumber(value).multipliedBy(100).toString();
                value = number + '%';
              } catch (e) {}
            }

            // Store RichText spans to initialize hyperlink tracking after Monaco is ready
            if (jsCellValue.kind === 'RichText' && jsCellValue.spans) {
              richTextSpans = jsCellValue.spans;
            }

            // open the calendar pick if the cell is a date
            if (!readOnly && ['Date', 'DateTime'].includes(jsCellValue.kind)) {
              gridSettings.setEditorInteractionState({
                ...gridSettings.getEditorInteractionState(),
                annotationState: `calendar${jsCellValue.kind === 'DateTime' ? '-time' : ''}`,
              });
            }
          } else {
            value = '';
          }
        }
      }

      this.codeCell = content.cellsSheet.tables.getCodeCellIntersects(location);
      if (this.codeCell?.language === 'Import' && changeToFormula) {
        // Formulas are allowed in the data region of editable (import) tables
        // (stored as single-cell code); only the table name and column-header
        // rows reject formulas.
        const isTableName = this.codeCell.show_name && location.y === this.codeCell.y;
        const isColumnHeader =
          this.codeCell.show_columns && location.y === this.codeCell.y + (this.codeCell.show_name ? 1 : 0);
        if (isTableName || isColumnHeader) {
          gridSettings.snackbar('Cannot create formula inside table', { severity: 'error' });
          this.closeIfOpen();
          return;
        }
      }

      if (cursorMode === undefined) {
        if (changeToFormula) {
          cursorMode = CursorMode.Enter;
        } else {
          cursorMode = value ? CursorMode.Edit : CursorMode.Enter;
        }
      }

      gridSettings.setInlineEditorState((prev) => ({
        ...prev,
        editMode: changeToFormula ? false : cursorMode === CursorMode.Edit,
      }));

      this.temporaryBold = this.formatSummary?.bold || undefined;
      this.temporaryItalic = this.formatSummary?.italic || undefined;
      this.temporaryUnderline = this.formatSummary?.underline || undefined;
      this.temporaryStrikeThrough = this.formatSummary?.strikeThrough || undefined;
      inlineEditorInput.set(value);
      inlineEditorInput.setReadOnly(readOnly);

      // Initialize hyperlink tracking after Monaco has the content
      if (richTextSpans) {
        inlineEditorSpans.setSpans(richTextSpans);
      }

      inlineEditorInput.setBackgroundColor(this.getCellBackgroundColor());
      inlineEditorInput.setTextColor(
        this.formatSummary.textColor
          ? convertColorStringToHex(this.formatSummary.textColor)
          : convertTintToHex(colors.cellFontColor)
      );
      inlineEditorInput.setCursorColor(convertTintToHex(colors.cellFontColor));
      this.updateFont();
      this.sendMultiplayerUpdate();
      this.showDiv();
      this.changeToFormula(changeToFormula);
      this.updateMonacoCursorPosition();

      gridSettings.setInlineEditorState((prev) => ({
        ...prev,
        readOnly,
      }));

      inlineEditorEvents.emit('status', true, value);

      // Manually trigger valueChanged if the initial value ends with : to show emoji dropdown
      // Only trigger if : is at the start or after whitespace (not after text like "test:")
      if (value.endsWith(':') && !this.formula && !readOnly) {
        const beforeColon = value.slice(0, -1);
        if (beforeColon === '' || beforeColon.match(/\s$/)) {
          // Small delay to ensure the editor is fully initialized
          setTimeout(() => {
            inlineEditorEvents.emit('valueChanged', value);
          }, 10);
        }
      }

      // this needs to be at the end to avoid a race condition where the cursor
      // draws at 0,0 when editing in a data table
      this.open = true;

      if (readOnly) {
        requestAnimationFrame(() => {
          if (this.open && this.readOnly) {
            this.updateMonacoCellLayout();
          }
        });
      }

      // Emit initial formatting state so toolbar reflects the current state
      this.updateSelectionFormatting();
    } else {
      this.close({});
    }
  };

  // Sends CellEdit updates to the multiplayer server.
  sendMultiplayerUpdate = () => {
    if (this.readOnly) {
      return;
    }
    multiplayer.sendCellEdit({
      text: inlineEditorInput.get(),
      cursor: inlineEditorInput.getCaret(),
      codeEditor: false,
      inlineCodeEditor: !!this.formula,
      bold: this.temporaryBold,
      italic: this.temporaryItalic,
      underline: this.temporaryUnderline,
      strikeThrough: this.temporaryStrikeThrough,
    });
  };

  toggleItalics = () => {
    if (this.temporaryItalic === undefined) {
      this.temporaryItalic = !this.formatSummary?.italic;
    } else {
      this.temporaryItalic = !this.temporaryItalic;
    }
    this.updateFont();
    this.sendMultiplayerUpdate();
  };

  toggleBold = () => {
    if (this.temporaryBold === undefined) {
      this.temporaryBold = !this.formatSummary?.bold;
    } else {
      this.temporaryBold = !this.temporaryBold;
    }
    this.updateFont();
    this.sendMultiplayerUpdate();
  };

  toggleUnderline = () => {
    if (this.temporaryUnderline === undefined) {
      this.temporaryUnderline = !this.formatSummary?.underline;
    } else {
      this.temporaryUnderline = !this.temporaryUnderline;
    }
    inlineEditorInput.setUnderline(this.temporaryUnderline);
    this.sendMultiplayerUpdate();
  };

  toggleStrikeThrough = () => {
    if (this.temporaryStrikeThrough === undefined) {
      this.temporaryStrikeThrough = !this.formatSummary?.strikeThrough;
    } else {
      this.temporaryStrikeThrough = !this.temporaryStrikeThrough;
    }
    inlineEditorInput.setStrikeThrough(this.temporaryStrikeThrough);
    this.sendMultiplayerUpdate();
  };

  /**
   * Check if the inline editor is open, not editing a formula, and has a text selection.
   */
  hasTextSelection(): boolean {
    return this.open && !this.formula && inlineEditorInput.hasSelection();
  }

  /**
   * Check if the inline editor is open and not editing a formula.
   */
  isOpenForText(): boolean {
    return this.open && !this.formula;
  }

  /**
   * Emit the selection formatting event based on the current selection or cursor position.
   * Called when the selection changes in the Monaco editor or when temporary formatting changes.
   */
  updateSelectionFormatting = () => {
    if (!this.open || this.formula) {
      inlineEditorEvents.emit('selectionFormatting', undefined);
      return;
    }

    if (inlineEditorInput.hasSelection()) {
      // If there's a selection, get the formatting from the spans
      const formatting = inlineEditorSpans.getSelectionFormattingSummary();
      inlineEditorEvents.emit('selectionFormatting', formatting);
    } else {
      // If no selection, check formatting at the cursor position first
      const cursorFormatting = inlineEditorSpans.getFormattingAtCursor();
      if (cursorFormatting) {
        // Use the span formatting at cursor position
        inlineEditorEvents.emit('selectionFormatting', cursorFormatting);
      } else {
        // Fall back to temporary formatting state (for when typing new text)
        const formatting = {
          bold: this.temporaryBold ?? this.formatSummary?.bold ?? false,
          italic: this.temporaryItalic ?? this.formatSummary?.italic ?? false,
          underline: this.temporaryUnderline ?? this.formatSummary?.underline ?? false,
          strikeThrough: this.temporaryStrikeThrough ?? this.formatSummary?.strikeThrough ?? false,
          textColor: this.formatSummary?.textColor ?? undefined,
        };
        inlineEditorEvents.emit('selectionFormatting', formatting);
      }
    }
  };

  /**
   * Get the current displayed state for a formatting property.
   * This checks span formatting at cursor first, then falls back to temporary formatting.
   */
  private getCurrentFormattingState(property: 'bold' | 'italic' | 'underline' | 'strikeThrough'): boolean {
    const cursorFormatting = inlineEditorSpans.getFormattingAtCursor();
    if (cursorFormatting) {
      return cursorFormatting[property] ?? false;
    }
    // Fall back to temporary formatting
    switch (property) {
      case 'bold':
        return this.temporaryBold ?? this.formatSummary?.bold ?? false;
      case 'italic':
        return this.temporaryItalic ?? this.formatSummary?.italic ?? false;
      case 'underline':
        return this.temporaryUnderline ?? this.formatSummary?.underline ?? false;
      case 'strikeThrough':
        return this.temporaryStrikeThrough ?? this.formatSummary?.strikeThrough ?? false;
    }
  }

  /**
   * Get the current text color state at the cursor position.
   * This checks span formatting at cursor first, then falls back to cell-level formatting.
   */
  private getCurrentTextColor(): string | undefined {
    const cursorFormatting = inlineEditorSpans.getFormattingAtCursor();
    if (cursorFormatting) {
      return cursorFormatting.textColor;
    }
    // Fall back to cell-level formatting
    return this.formatSummary?.textColor ?? undefined;
  }

  /**
   * Get the current formatting state for all properties at the cursor position.
   * Used when creating empty spans to preserve existing formatting.
   */
  private getCurrentFormattingStateAll(): SpanFormatting {
    return {
      bold: this.getCurrentFormattingState('bold'),
      italic: this.getCurrentFormattingState('italic'),
      underline: this.getCurrentFormattingState('underline'),
      strikeThrough: this.getCurrentFormattingState('strikeThrough'),
      textColor: this.getCurrentTextColor(),
    };
  }

  /**
   * Get the current formatting state for all properties at the cursor position.
   * Public version for use when creating hyperlinks that should inherit formatting.
   */
  getFormattingStateForHyperlink(): SpanFormatting {
    return this.getCurrentFormattingStateAll();
  }

  /**
   * Toggle bold formatting for the current selection or create empty span.
   * Returns true if formatting was handled by the inline editor, false otherwise.
   */
  toggleBoldForSelection = (): boolean => {
    if (!this.isOpenForText()) {
      return false;
    }
    // If there's a selection, apply span formatting
    if (inlineEditorInput.hasSelection()) {
      // Pass cell-level bold so toggle knows the "visual" state when no span exists
      inlineEditorSpans.toggleFormattingForSelection('bold', this.formatSummary?.bold ?? undefined);
      this.updateFont(); // Switch to CSS-based bold/italic when spans are active
      this.updateSelectionFormatting();
      return true;
    }
    // No selection - create an empty span at cursor with toggled formatting
    // Preserve all other formatting properties from the current position
    const currentFormatting = this.getCurrentFormattingStateAll();
    inlineEditorSpans.createEmptySpanAtCursor({
      ...currentFormatting,
      bold: !currentFormatting.bold,
    });
    this.updateFont(); // Switch to CSS-based bold/italic when spans are active
    this.updateSelectionFormatting();
    return true;
  };

  /**
   * Toggle italic formatting for the current selection or create empty span.
   * Returns true if formatting was handled by the inline editor, false otherwise.
   */
  toggleItalicForSelection = (): boolean => {
    if (!this.isOpenForText()) {
      return false;
    }
    // If there's a selection, apply span formatting
    if (inlineEditorInput.hasSelection()) {
      // Pass cell-level italic so toggle knows the "visual" state when no span exists
      inlineEditorSpans.toggleFormattingForSelection('italic', this.formatSummary?.italic ?? undefined);
      this.updateFont(); // Switch to CSS-based bold/italic when spans are active
      this.updateSelectionFormatting();
      return true;
    }
    // No selection - create an empty span at cursor with toggled formatting
    const currentFormatting = this.getCurrentFormattingStateAll();
    inlineEditorSpans.createEmptySpanAtCursor({
      ...currentFormatting,
      italic: !currentFormatting.italic,
    });
    this.updateFont(); // Switch to CSS-based bold/italic when spans are active
    this.updateSelectionFormatting();
    return true;
  };

  /**
   * Toggle underline formatting for the current selection or create empty span.
   * Returns true if formatting was handled by the inline editor, false otherwise.
   */
  toggleUnderlineForSelection = (): boolean => {
    if (!this.isOpenForText()) {
      return false;
    }
    // If there's a selection, apply span formatting
    if (inlineEditorInput.hasSelection()) {
      // Pass cell-level underline so toggle knows the "visual" state when no span exists
      inlineEditorSpans.toggleFormattingForSelection('underline', this.formatSummary?.underline ?? undefined);
      this.updateSelectionFormatting();
      return true;
    }
    // No selection - create an empty span at cursor with toggled formatting
    const currentFormatting = this.getCurrentFormattingStateAll();
    inlineEditorSpans.createEmptySpanAtCursor({
      ...currentFormatting,
      underline: !currentFormatting.underline,
    });
    this.updateSelectionFormatting();
    return true;
  };

  /**
   * Toggle strikethrough formatting for the current selection or create empty span.
   * Returns true if formatting was handled by the inline editor, false otherwise.
   */
  toggleStrikeThroughForSelection = (): boolean => {
    if (!this.isOpenForText()) {
      return false;
    }
    // If there's a selection, apply span formatting
    if (inlineEditorInput.hasSelection()) {
      // Pass cell-level strikeThrough so toggle knows the "visual" state when no span exists
      inlineEditorSpans.toggleFormattingForSelection('strikeThrough', this.formatSummary?.strikeThrough ?? undefined);
      this.updateSelectionFormatting();
      return true;
    }
    // No selection - create an empty span at cursor with toggled formatting
    const currentFormatting = this.getCurrentFormattingStateAll();
    inlineEditorSpans.createEmptySpanAtCursor({
      ...currentFormatting,
      strikeThrough: !currentFormatting.strikeThrough,
    });
    this.updateSelectionFormatting();
    return true;
  };

  /**
   * Set text color for the current selection or create empty span.
   * Returns true if formatting was handled by the inline editor, false otherwise.
   */
  setTextColorForSelection = (color: string | undefined): boolean => {
    if (!this.isOpenForText()) {
      return false;
    }
    // If there's a selection, apply span formatting
    if (inlineEditorInput.hasSelection()) {
      inlineEditorSpans.applyFormattingToSelection({ textColor: color });
      this.updateSelectionFormatting();
      return true;
    }
    // No selection - create an empty span at cursor with the new text color
    // Preserve all other formatting properties from the current position
    const currentFormatting = this.getCurrentFormattingStateAll();
    inlineEditorSpans.createEmptySpanAtCursor({
      ...currentFormatting,
      textColor: color,
    });
    this.updateSelectionFormatting();
    return true;
  };

  /**
   * Clear span formatting when the inline editor is open (not editing a formula).
   * If there's a selection, clears formatting only for the selected text.
   * If no selection but cursor is in a span, clears that span's formatting.
   * Does not clear cell-level formatting - only span-level formatting.
   * Returns true if formatting was cleared, false otherwise.
   */
  clearSpanFormatting = (): boolean => {
    if (!this.open || this.formula) {
      return false;
    }
    // If there's a selection, clear formatting only for the selection
    if (inlineEditorInput.hasSelection()) {
      inlineEditorSpans.clearFormattingForSelection();
      this.updateFont(); // May switch back to font-family-based bold/italic if spans are cleared
      this.updateSelectionFormatting();
      return true;
    }
    // If no selection, try to clear the span at the cursor position
    if (inlineEditorSpans.clearFormattingAtCursor()) {
      this.updateFont(); // May switch back to font-family-based bold/italic if spans are cleared
      this.updateSelectionFormatting();
      return true;
    }
    // No span at cursor - don't clear cell-level formatting
    return false;
  };

  private updateFont = () => {
    if (!this.formula) {
      const italic = this.temporaryItalic === undefined ? this.formatSummary?.italic : this.temporaryItalic;
      const bold = this.temporaryBold === undefined ? this.formatSummary?.bold : this.temporaryBold;

      // When there are active spans (RichText), use base font and CSS for bold/italic
      // so span-level formatting can override cell-level formatting.
      // When there are no spans (plain text with cell-level formatting), set the font family
      // directly so Monaco can use correct font metrics for cursor positioning.
      if (inlineEditorSpans.isActive()) {
        inlineEditorInput.setFontFamily(`OpenSans, ${EMOJI_FONTS}`);
        inlineEditorInput.setItalic(!!italic);
        inlineEditorInput.setBold(!!bold);
      } else {
        // Set font family directly based on bold/italic state for correct cursor positioning
        const fontFamily = this.getFontFamily(!!bold, !!italic);
        inlineEditorInput.setFontFamily(fontFamily);
        // Clear CSS data attributes since we're using font family directly
        inlineEditorInput.setItalic(false);
        inlineEditorInput.setBold(false);
      }
    } else {
      inlineEditorInput.setFontFamily(`OpenSans, ${EMOJI_FONTS}`);
      inlineEditorInput.setItalic(false);
      inlineEditorInput.setBold(false);
    }

    // Set font size from format summary
    const fontSize = this.formatSummary?.fontSize ?? DEFAULT_FONT_SIZE;
    inlineEditorInput.setFontSize(fontSize);
  };

  /**
   * Get the font family name based on bold and italic formatting.
   */
  private getFontFamily = (bold: boolean, italic: boolean): string => {
    if (bold && italic) {
      return `OpenSans-BoldItalic, ${EMOJI_FONTS}`;
    } else if (bold) {
      return `OpenSans-Bold, ${EMOJI_FONTS}`;
    } else if (italic) {
      return `OpenSans-Italic, ${EMOJI_FONTS}`;
    }
    return `OpenSans, ${EMOJI_FONTS}`;
  };

  // Refreshes the format summary and updates the font display in the inline editor
  refreshFontSize = async () => {
    if (!this.location) return;

    this.formatSummary = await quadraticCore.getCellFormatSummary(
      this.location.sheetId,
      this.location.x,
      this.location.y
    );

    this.updateFont();
    this.updateMonacoCellLayout();
  };

  // Handles updates to the Monaco editor cursor position
  updateMonacoCursorPosition = () => {
    // this will get called upon opening (before variables are set), and after every cursor movement
    if (!this.location) return;

    const value = inlineEditorInput.get();
    if (value[0] === '=') {
      this.changeToFormula(true);
    } else if (this.formula && value[0] !== '=') {
      this.changeToFormula(false);
    }

    this.updateMonacoCellLayout();

    if (this.formula && this.location) {
      updateFormulaHighlights(value, this.location);
    }

    // Clean up empty spans that the cursor has moved away from
    if (!this.formula) {
      inlineEditorSpans.cleanupEmptySpans();
    }

    // Check if cursor is over a hyperlink and emit event for popup
    this.checkCursorOnHyperlink();

    // Update formatting buttons to reflect the span at the cursor position
    this.updateSelectionFormatting();

    this.sendMultiplayerUpdate();
  };

  // Check if the cursor is over a hyperlink and emit event to show/hide popup
  private checkCursorOnHyperlink = () => {
    if (this.formula) {
      events.emit('inlineEditorCursorOnHyperlink', undefined);
      return;
    }

    if (!this.location) return;

    const offset = inlineEditorInput.getCaret();
    const hyperlinkSpan = inlineEditorSpans.getHyperlinkAtOffset(offset);
    if (hyperlinkSpan?.link) {
      const sheet = sheets.sheet;
      const cursorLoc = this.cursorLocation ?? this.location;
      const mergeRect = sheet.getMergeCellRect(cursorLoc.x, cursorLoc.y);
      let rect: Rectangle;
      if (mergeRect) {
        rect = sheet.getScreenRectangle(
          Number(mergeRect.min.x),
          Number(mergeRect.min.y),
          Number(mergeRect.max.x) - Number(mergeRect.min.x) + 1,
          Number(mergeRect.max.y) - Number(mergeRect.min.y) + 1
        );
      } else {
        const offsets = sheet.getCellOffsets(cursorLoc.x, cursorLoc.y);
        rect = new Rectangle(offsets.x, offsets.y, offsets.width, offsets.height);
      }
      const fullText = inlineEditorInput.get();
      const linkText = fullText.slice(hyperlinkSpan.start, hyperlinkSpan.end);
      events.emit('inlineEditorCursorOnHyperlink', { url: hyperlinkSpan.link, rect, linkText });
    } else {
      events.emit('inlineEditorCursorOnHyperlink', undefined);
    }
  };

  updateMonacoCellLayout = () => {
    if (!this.location || !this.cursorLocation) return;

    // Use the cursor location (where user clicked) for positioning the editor,
    // expanded to the full merged-cell rect when applicable.
    const cellBounds = this.getCursorCellBounds();
    if (!cellBounds) return;

    // When the cell is in a frozen pane, shift its origin to the pinned
    // (displayed) world position. The editor div lives in the viewport-
    // transformed overlay layer, so without this shift it would scroll away
    // from the pinned cell with the body on the frozen axis/axes.
    const displayOrigin = frozenPanes.getDisplayWorld(new Point(cellBounds.x, cellBounds.y));
    const { width, height } = cellBounds;
    const x = displayOrigin.x;
    const y = displayOrigin.y;
    const cellOutlineOffset = CURSOR_THICKNESS * (this.formula ? 0.5 : 1);
    const cellContentWidth = width - cellOutlineOffset * 2;
    const cellContentHeight = height - cellOutlineOffset * 2;
    const align = this.formatSummary?.align ?? 'left';
    let verticalAlign = this.formatSummary?.verticalAlign ?? 'bottom';
    const wrap = this.readOnly ? 'wrap' : (this.formatSummary?.wrap ?? (this.codeCell ? 'clip' : 'overflow'));
    const underline = this.temporaryUnderline ?? this.formatSummary?.underline ?? false;
    const strikeThrough = this.temporaryStrikeThrough ?? this.formatSummary?.strikeThrough ?? false;

    // For default-sized cells, override vertical alignment to 'middle' to match
    // the centering behavior in CellLabel.ts for rendered text.
    const defaultExtraSpace = CELL_HEIGHT - LINE_HEIGHT;
    if (height - LINE_HEIGHT <= defaultExtraSpace) {
      verticalAlign = 'middle';
    }

    const viewportRect = pixiApp.getViewportRectangle();
    const anchorX = cellOutlineOffset + x;
    const anchorY = cellOutlineOffset + y;
    // Only code-table previews render the action bar (Chat / Edit Code). Plain
    // read-only previews (a viewer selecting text to copy) have no bar, so they
    // don't need space reserved below the editor for one.
    const readOnlyActionBarHeight =
      this.readOnly && this.tableCodeCell ? READ_ONLY_CODE_TABLE_ACTION_BAR_RESERVED_HEIGHT : 0;
    const viewportBounds: InlineEditorViewportBounds = {
      maxWidth: viewportRect.right - anchorX - PADDING_FOR_INLINE_EDITOR,
      maxHeight: viewportRect.bottom - anchorY - PADDING_FOR_INLINE_EDITOR - readOnlyActionBarHeight,
    };

    // Previews are sized to the max of the measured text and the cell width.
    const layoutContentWidth = cellContentWidth;

    let inlineEditorWidth = 0;
    let inlineEditorHeight = 0;
    const layoutEditor = (bounds: InlineEditorViewportBounds) => {
      const layout = inlineEditorInput.updateTextLayout(
        layoutContentWidth,
        cellContentHeight,
        align,
        verticalAlign,
        wrap,
        underline,
        strikeThrough,
        this.formula ?? false,
        bounds,
        this.readOnly
      );
      inlineEditorWidth = layout.width;
      inlineEditorHeight = layout.height;
    };

    layoutEditor(viewportBounds);

    // Calculate x position based on horizontal alignment
    if (this.readOnly) {
      this.x = anchorX;
    } else if (align === 'right') {
      this.x = cellOutlineOffset + Math.min(x, x + cellContentWidth - inlineEditorWidth);
    } else if (align === 'center') {
      // Center the editor over the cell (works for both fitting and overflowing content)
      this.x = cellOutlineOffset + x + (cellContentWidth - inlineEditorWidth) / 2;
    } else {
      this.x = cellOutlineOffset + x;
    }

    // Grow down from the cell top when content is taller than the cell
    if (inlineEditorHeight > cellContentHeight) {
      this.y = anchorY;
    } else {
      this.y =
        cellOutlineOffset + (verticalAlign === 'bottom' ? Math.min(y, y + cellContentHeight - inlineEditorHeight) : y);
    }

    // Keep the editor on screen; re-layout if horizontal space shrinks after positioning
    const padding = PADDING_FOR_INLINE_EDITOR;
    const clampedX = Math.max(viewportRect.left, Math.min(this.x, viewportRect.right - padding - inlineEditorWidth));
    if (!this.readOnly && clampedX !== this.x) {
      this.x = clampedX;
      const adjustedMaxWidth = viewportRect.right - this.x - padding;
      if (adjustedMaxWidth < inlineEditorWidth) {
        layoutEditor({ ...viewportBounds, maxWidth: adjustedMaxWidth });
        if (align === 'right') {
          this.x = cellOutlineOffset + Math.min(x, x + cellContentWidth - inlineEditorWidth);
        } else if (align === 'center') {
          this.x = cellOutlineOffset + x + (cellContentWidth - inlineEditorWidth) / 2;
        }
        this.x = Math.max(viewportRect.left, Math.min(this.x, viewportRect.right - padding - inlineEditorWidth));
      }
    }
    this.y = Math.max(
      viewportRect.top,
      Math.min(this.y, viewportRect.bottom - padding - readOnlyActionBarHeight - inlineEditorHeight)
    );

    const finalMaxHeight = viewportRect.bottom - this.y - padding - readOnlyActionBarHeight;
    if (finalMaxHeight < inlineEditorHeight) {
      const finalMaxWidth = viewportRect.right - this.x - padding;
      layoutEditor({ maxWidth: finalMaxWidth, maxHeight: finalMaxHeight });
      if (inlineEditorHeight > cellContentHeight) {
        this.y = anchorY;
      } else {
        this.y =
          cellOutlineOffset +
          (verticalAlign === 'bottom' ? Math.min(y, y + cellContentHeight - inlineEditorHeight) : y);
      }
      this.y = Math.max(
        viewportRect.top,
        Math.min(this.y, viewportRect.bottom - padding - readOnlyActionBarHeight - inlineEditorHeight)
      );
    }

    this.width = inlineEditorWidth;
    this.height = inlineEditorHeight + OPEN_SANS_FIX.y / 3;

    // The pin baseline, viewport snapshot, and re-attach must only update while
    // the editor is attached to its cell. If this layout runs while detached
    // (the user scrolled/zoomed away, or the return-to-cell animation is in
    // flight), recomputing the pin from the live (scrolled) transform and
    // forcing setDetached(false) would yank the floating editor to the wrong
    // place mid-animation. While detached we still update the editor size
    // (this.x/y/width/height above) but leave the pin/attachment alone.
    if (!this.detached) {
      // Capture the editor's current screen position as the baseline that
      // onViewportChanged pins to while scrolling. this.x/this.y are the final
      // (clamped) world coordinates, so map them through the live viewport
      // transform and clear any leftover per-frame transform.
      pixiApp.viewport.updateTransform();
      const wt = pixiApp.viewport.worldTransform;
      this.pinScreenX = wt.a * this.x + wt.tx;
      this.pinScreenY = wt.d * this.y + wt.ty;
      if (this.div) {
        this.div.style.transform = '';
      }
      // Snapshot the viewport so returnToCell() can restore exactly this
      // position/zoom after the user scrolls or zooms away.
      this.attachedViewport = {
        centerX: pixiApp.viewport.center.x,
        centerY: pixiApp.viewport.center.y,
        scale: pixiApp.viewport.scale.x,
      };
    }

    gridSettings.setInlineEditorState((prev) => ({
      ...prev,
      left: this.x,
      top: this.y,
      width: this.width,
      height: this.height,
    }));

    events.emit('setDirty', { cursor: true });
  };

  // Toggle between normal editor and formula editor.
  private changeToFormula = (formula: boolean) => {
    if (this.formula === formula) return;
    if (this.codeCell?.language === 'Import' && formula) {
      // Formulas are allowed in the data region of editable (import) tables;
      // only the table name and column-header rows reject formulas.
      const isTableName = this.codeCell.show_name && this.location?.y === this.codeCell.y;
      const isColumnHeader =
        this.codeCell.show_columns && this.location?.y === this.codeCell.y + (this.codeCell.show_name ? 1 : 0);
      if (isTableName || isColumnHeader) {
        gridSettings.snackbar('Cannot create formula inside table', { severity: 'error' });
        this.closeIfOpen();
        return;
      }
    }
    this.formula = formula;
    if (formula) {
      inlineEditorInput.setLanguage('Formula');

      // need to show the change to A1 notation
      events.emit('setDirty', { headings: true });
    } else {
      inlineEditorInput.setLanguage('inline-editor');
    }

    gridSettings.setInlineEditorState((prev) => ({
      ...prev,
      formula,
    }));

    if (formula && this.location) {
      updateFormulaHighlights(inlineEditorInput.get(), this.location);
    }
    this.updateFont();
  };

  closeIfOpen = async () => {
    if (this.open) {
      await this.close({});
    }
  };

  validateInput = async (): Promise<string | undefined> => {
    if (!this.open || !this.location || this.formula || this.readOnly) return;
    const value = inlineEditorInput.get();
    const validationError = await quadraticCore.validateInput(
      this.location.sheetId,
      this.location.x,
      this.location.y,
      value
    );
    return validationError;
  };

  // Close editor. It saves the value if cancel = false. It also moves the
  // cursor by (deltaX, deltaY).
  // @returns whether the editor closed successfully
  close = async ({
    deltaX = 0,
    deltaY = 0,
    cancel = false,
    skipChangeSheet = false,
    skipFocusGrid = false,
  }: {
    deltaX?: number;
    deltaY?: number;
    cancel?: boolean;
    skipChangeSheet?: boolean;
    skipFocusGrid?: boolean;
  }): Promise<boolean> => {
    if (!this.open) return true;
    if (this.readOnly) {
      cancel = true;
    }
    if (!this.location) {
      throw new Error('Expected location to be defined in InlineEditorHandler');
    }
    let value = inlineEditorInput.get();

    if (this.cursorIsMoving) {
      inlineEditorKeyboard.resetKeyboardPosition(true);
    }

    // Return to the edited cell's sheet (the user may be on another sheet
    // picking a formula reference) so the committed or cancelled edit is shown.
    if (!skipChangeSheet) {
      sheets.current = this.location.sheetId;
    }

    if (!cancel) {
      if (this.formula) {
        const updatedValue = inlineEditorFormula.closeParentheses();
        if (updatedValue) value = updatedValue;
        trackFunctionsFromFormula(value.slice(1));
        quadraticCore.setCodeCellValue({
          sheetId: this.location.sheetId,
          x: this.location.x,
          y: this.location.y,
          language: 'Formula',
          codeString: value.slice(1),
          isAi: false,
        });
        trackEvent('[CodeEditor].cellRun', {
          type: 'Formula',
          inline: true,
        });
      } else {
        const location = { ...this.location };
        const validationError = await this.validateInput();
        if (validationError) {
          events.emit('hoverCell', { x: this.location.x, y: this.location.y, validationId: validationError, value });
          // need to change the sheet back to the original sheet if there's a validation error
          if (skipChangeSheet) {
            sheets.current = location.sheetId;
          }
          return false;
        } else {
          // Check for URLs at cursor position and auto-convert to hyperlinks
          inlineEditorSpans.checkAutoLinkOnClose();

          const trimmedValue = value.trim();

          // A stray value edit can land on a multi-cell code/data table's anchor
          // during a rapid click/drag race. Writing a static value there would
          // replace and destroy the entire table, so discard the edit and open
          // the code editor for that table instead of overwriting it. (Typing
          // never legitimately opens a value editor on a table anchor; code
          // cells always route to the code editor.)
          if (isMultiCellTableAnchor({ x: location.x, y: location.y })) {
            this.reset();
            gridSettings.changeInput(false);
            await openCodeEditorForCell();
            return true;
          }

          // Check if we have formatted spans (e.g., hyperlinks) that require RichText
          if (inlineEditorSpans.isActive() && inlineEditorSpans.hasFormattedSpans()) {
            // Calculate leading whitespace offset to adjust span positions
            const leadingWhitespace = value.length - value.trimStart().length;
            const spans = inlineEditorSpans.buildTextSpans(trimmedValue, leadingWhitespace);
            quadraticCore.setCellRichText(location.sheetId, location.x, location.y, spans);
          } else {
            quadraticCore.setCellValue(location.sheetId, location.x, location.y, trimmedValue, false);
          }
          if (!skipChangeSheet) {
            events.emit('hoverCell');
          }
        }
      }
    }

    this.reset();
    gridSettings.changeInput(false);

    // Update Grid Interaction state, reset input value state
    if (deltaX || deltaY) {
      const target = applyDeltaSkippingHidden(sheets.sheet.cursor.position, deltaX, deltaY);
      sheets.sheet.cursor.moveTo(target.x, target.y);
    }

    // Set focus back to Grid
    if (!skipFocusGrid) {
      focusGrid();
    }
    return true;
  };

  chatAboutTable = () => {
    if (!this.tableCodeCell) {
      throw new Error('Expected tableCodeCell to be defined in chatAboutTable');
    }

    const { sheetId, pos } = this.tableCodeCell;
    const tableName = content.cellsSheets.getById(sheetId)?.tables.getCodeCellIntersects(pos)?.name;
    if (!tableName) {
      console.warn('chatAboutTable: could not find table name for code cell');
      this.closeIfOpen();
      return;
    }

    // Also reference the specific cell the preview was opened from, so both the
    // user and the AI know which cell brought them to the chat.
    let reference = tableName;
    const cellLocation = this.cursorLocation;
    if (cellLocation) {
      const sheetName = sheets.getById(cellLocation.sheetId)?.name;
      const cellA1 = xyToA1(cellLocation.x, cellLocation.y);
      const cellRef = sheetName ? `${sheetName}!${cellA1}` : cellA1;
      reference = `${tableName} @${cellRef}`;
    }

    defaultActionSpec[Action.AddReferenceToAIAnalyst].run(reference);
  };

  openTableCodeEditor = () => {
    if (!this.tableCodeCell) {
      throw new Error('Expected tableCodeCell to be defined in openTableCodeEditor');
    }
    gridSettings.updateCodeEditorState((prev) => ({
      ...prev,
      showCodeEditor: true,
      escapePressed: false,
      diffEditorContent: undefined,
      waitingForEditorClose: {
        codeCell: this.tableCodeCell!,
        showCellTypeMenu: false,
        initialCode: '',
      },
    }));
    this.close({ cancel: true });
  };

  getTextAlign(): CellAlign {
    return this.formatSummary?.align ?? 'left';
  }

  getTextWrap(): CellWrap {
    if (this.readOnly) return 'wrap';
    return this.formatSummary?.wrap ?? (this.codeCell ? 'clip' : 'overflow');
  }

  // Attaches the inline editor container created by React in InlineEditor.tsx
  attach(div: HTMLDivElement) {
    this.div = div;
    this.close({ cancel: true });
  }

  detach() {
    this.div = undefined;
  }

  getTableCodeCell(): CodeCell | undefined {
    return this.tableCodeCell;
  }

  // Returns whether we are editing a formula.
  isEditingFormula() {
    return this.open && this.formula;
  }

  // True when F2 / toggle-arrow-mode should not apply (formula cells use contextual arrows).
  isToggleArrowModeBlocked() {
    if (this.isEditingFormula()) return true;
    const pos = sheets.sheet.cursor.position;
    return content.cellsSheet.tables.getCodeCellIntersects(pos)?.language === 'Formula';
  }

  isReadOnlyPreview() {
    return this.open && this.readOnly;
  }

  // This checks whether the inline editor is showing (or showing at a given location)
  // Returns the cursor location (not the edit location) for positioning purposes
  getShowing(x?: number, y?: number): SheetPosTS | undefined {
    if (
      this.open &&
      (x === undefined || y === undefined || (this.cursorLocation?.x === x && this.cursorLocation.y === y))
    ) {
      return this.cursorLocation;
    }
  }

  isOpen(): boolean {
    return this.open;
  }

  // True when a formula is being edited and the user is viewing a different
  // sheet than the one the formula cell is on.
  isOffSheet(): boolean {
    return this.open && !!this.formula && !!this.location && sheets.current !== this.location.sheetId;
  }

  // Whether the given DOM node is inside the inline editor's overlay. Window-
  // level "click outside to commit" handlers use this so a click on the
  // editor's own UI (e.g. the detached return-to-cell label) doesn't close the
  // editor before that UI's own handler runs.
  containsElement(node: EventTarget | null | undefined): boolean {
    return !!this.div && node instanceof Node && this.div.contains(node);
  }

  private showDiv = () => {
    if (!this.div) {
      throw new Error('Expected div to be defined in showDiv');
    }

    gridSettings.setInlineEditorState((prev) => ({
      ...prev,
      visible: true,
      offSheet: false,
    }));
    this.showing = true;
  };

  // Hides the overlay while a formula is edited from another sheet, keeping
  // the editor mounted and focusable (see InlineEditorState.offSheet).
  private hideDivOffSheet = () => {
    gridSettings.setInlineEditorState((prev) => ({
      ...prev,
      visible: false,
      offSheet: true,
    }));
    this.showing = false;
  };

  private hideDiv = () => {
    // Deliberately no `div` assertion. A missing div just means React unmounted
    // the overlay: `detach()` clears `div` but leaves `open`/`showing` set, so
    // navigating off the file route with the editor open leaves this handler
    // (a singleton that outlives the route) in that state. The window-level
    // `mouseup` listener in Pointer.pointerUpOutsideViewport then still calls
    // `close()` from the dashboard, and throwing here escaped the async `close()`
    // as an unhandled rejection — which also left `open` stuck true, so every
    // later click threw again. Nothing below needs the div, and clearing the
    // state is exactly what a close should do.
    gridSettings.setInlineEditorState((prev) => ({
      ...prev,
      visible: false,
      offSheet: false,
      readOnly: false,
    }));
    this.showing = false;
  };

  // Called when manually changing cell position via clicking on a new cell
  // (except when editing formula). Returns whether the editor can be closed
  // (ie, if it fails validation with a ValidationStyle::Stop, we do not let it
  // close)
  async handleCellPointerDown(): Promise<boolean> {
    if (this.open) {
      // If cursorIsMoving is true, the user is actively selecting cells for a
      // reference, so we should allow them to continue changing the reference
      // even if wantsCellRef() returns false (e.g., cursor is right after a
      // completed reference like "TableName[Col]|")
      if (!this.formula || (!this.cursorIsMoving && !inlineEditorFormula.wantsCellRef())) {
        return await this.close({});
      } else {
        if (!this.cursorIsMoving) {
          this.cursorIsMoving = true;
          inlineEditorFormula.addInsertingCells(inlineEditorInput.getCaret());
        }
      }
    }
    return true;
  }

  private replaceText = (text: string, highlight: boolean | number) => {
    if (!this.open) return;
    inlineEditorInput.set(text, highlight);
  };
}

export const inlineEditorHandler = new InlineEditorHandler();
